import { useMemo, useState } from 'react';
import { RelativeTime } from '@jywer/ui';
import {
  issueServiceToken as issueServiceTokenApi,
  revokeServiceToken as revokeServiceTokenApi,
} from './api';
import type { IssuedServiceToken, ServiceTokenRow, UserRow } from './types';

export function ServiceTokensTab({
  tokens,
  users,
  onTokensChange,
}: {
  tokens: ServiceTokenRow[];
  users: UserRow[];
  onTokensChange: (tokens: ServiceTokenRow[]) => void;
}) {
  const [showRevoked, setShowRevoked] = useState(false);
  const [userFilter, setUserFilter] = useState<string>('');
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState<IssuedServiceToken | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const visible = useMemo(() => {
    return tokens
      .filter((t) => (showRevoked ? true : !t.revokedAt))
      .filter((t) => (userFilter ? t.boundUser.id === userFilter : true))
      .sort((a, b) => {
        const al = a.lastUsedAt ?? a.createdAt;
        const bl = b.lastUsedAt ?? b.createdAt;
        return al < bl ? 1 : al > bl ? -1 : 0;
      });
  }, [tokens, showRevoked, userFilter]);

  async function handleRevoke(t: ServiceTokenRow) {
    if (
      !confirm(
        `Tilbagekald token for ${t.boundUser.displayName}? Dette vil ikke kunne fortrydes — token stopper med at virke inden for 30 sekunder.`,
      )
    )
      return;
    setErr(null);
    try {
      await revokeServiceTokenApi(t.id);
      onTokensChange(
        tokens.map((x) => (x.id === t.id ? { ...x, revokedAt: new Date().toISOString() } : x)),
      );
    } catch (x) {
      setErr(x instanceof Error ? x.message : 'Tilbagekaldelse fejlede');
    }
  }

  return (
    <div>
      <div className="mb-3 flex items-center gap-3">
        <select
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
          className="rounded border border-line-2 px-2 py-1 text-sm"
        >
          <option value="">Alle brugere</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.display_name} ({u.email})
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-ink-1">
          <input
            type="checkbox"
            checked={showRevoked}
            onChange={(e) => setShowRevoked(e.target.checked)}
          />
          Vis tilbagekaldte
        </label>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setIssuing(true)}
          className="rounded bg-primary px-3 py-1 text-sm text-white"
        >
          Udstedt nyt token
        </button>
      </div>

      {err && <p className="mb-3 text-sm text-red-700">{err}</p>}

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line-2 text-left text-xs uppercase text-ink-2">
            <th className="py-2">Bundet bruger</th>
            <th>Navn</th>
            <th>Token ID</th>
            <th>Udstedt</th>
            <th>Sidst brugt</th>
            <th>Status</th>
            <th className="text-right">Handlinger</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((t) => (
            <tr key={t.id} className="border-b border-line-1">
              <td className="py-2">
                <div>{t.boundUser.displayName}</div>
                <div className="text-xs text-ink-2">{t.boundUser.email}</div>
              </td>
              <td className="text-ink-1">{t.name ?? '—'}</td>
              <td>
                <code className="text-xs">…{t.accessTokenId.slice(-8)}</code>
              </td>
              <td className="text-xs text-ink-2">
                <RelativeTime value={t.createdAt} />
              </td>
              <td className="text-xs text-ink-2">
                {t.lastUsedAt ? <RelativeTime value={t.lastUsedAt} /> : 'Aldrig'}
              </td>
              <td>
                {t.revokedAt ? (
                  <span className="text-red-700">Tilbagekaldt</span>
                ) : (
                  <span className="text-green-700">Aktiv</span>
                )}
              </td>
              <td className="text-right">
                {!t.revokedAt && (
                  <button
                    type="button"
                    onClick={() => void handleRevoke(t)}
                    className="text-xs text-red-700 underline"
                  >
                    Tilbagekald
                  </button>
                )}
              </td>
            </tr>
          ))}
          {visible.length === 0 && (
            <tr>
              <td colSpan={7} className="py-6 text-center text-ink-2">
                Ingen tokens matcher filteret.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {issuing && (
        <IssueTokenModal
          users={users}
          onCancel={() => setIssuing(false)}
          onIssued={(result) => {
            setIssuing(false);
            setIssued(result);
            // Push a new placeholder row so the list reflects the creation
            // until the parent re-fetches.
            onTokensChange([
              {
                id: result.id,
                accessTokenId: result.accessTokenId,
                name: null,
                boundUser: result.boundUser,
                issuedByUser: null,
                createdAt: new Date().toISOString(),
                revokedAt: null,
                lastUsedAt: null,
              },
              ...tokens,
            ]);
          }}
        />
      )}

      {issued && <IssuedSecretModal issued={issued} onClose={() => setIssued(null)} />}
    </div>
  );
}

function IssueTokenModal({
  users,
  onCancel,
  onIssued,
}: {
  users: UserRow[];
  onCancel: () => void;
  onIssued: (result: IssuedServiceToken) => void;
}) {
  const activeUsers = users.filter((u) => u.active);
  const [boundUserId, setBoundUserId] = useState(activeUsers[0]?.id ?? '');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!boundUserId) return;
    setBusy(true);
    setErr(null);
    try {
      const result = await issueServiceTokenApi(boundUserId, name.trim() || undefined);
      onIssued(result);
    } catch (x) {
      setErr(x instanceof Error ? x.message : 'Udstedelse fejlede');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/30"
      onClick={onCancel}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => void handleSubmit(e)}
        className="w-[440px] rounded bg-paper-0 p-6 shadow-xl"
      >
        <h3 className="mb-4 text-lg font-semibold text-primary">Udstedt nyt Service Token</h3>
        <label className="mb-3 block text-sm">
          <span className="mb-1 block text-ink-2">Bundet bruger</span>
          <select
            value={boundUserId}
            onChange={(e) => setBoundUserId(e.target.value)}
            className="w-full rounded border border-line-2 px-2 py-1"
            required
          >
            {activeUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.display_name} ({u.email})
              </option>
            ))}
          </select>
        </label>
        <label className="mb-4 block text-sm">
          <span className="mb-1 block text-ink-2">Navn (valgfrit)</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="fx 'jywer-skill — Daniel'"
            className="w-full rounded border border-line-2 px-2 py-1"
          />
        </label>
        {err && <p className="mb-3 text-sm text-red-700">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded border px-3 py-1 text-sm">
            Annullér
          </button>
          <button
            type="submit"
            disabled={busy || !boundUserId}
            className="rounded bg-primary px-3 py-1 text-sm text-white disabled:opacity-60"
          >
            {busy ? 'Udstedt…' : 'Udstedt'}
          </button>
        </div>
      </form>
    </div>
  );
}

function IssuedSecretModal({
  issued,
  onClose,
}: {
  issued: IssuedServiceToken;
  onClose: () => void;
}) {
  function copy(value: string) {
    void navigator.clipboard.writeText(value);
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-[520px] rounded bg-paper-0 p-6 shadow-2xl">
        <h3 className="mb-2 text-lg font-semibold text-primary">Token udstedt</h3>
        <p className="mb-4 rounded border border-status-warn/40 bg-status-warn-tint px-3 py-2 text-sm text-status-warn">
          <strong>Gem disse nu — de vises ikke igen.</strong> Kopiér Client ID + Client Secret til
          et sikkert sted (password manager). Når du lukker dette vindue, kan secret ikke genhentes.
        </p>
        <label className="mb-3 block text-sm">
          <span className="mb-1 block text-ink-2">Client ID</span>
          <div className="flex gap-2">
            <input
              readOnly
              value={issued.accessTokenId}
              className="flex-1 rounded border border-line-2 bg-paper-1 px-2 py-1 font-mono text-xs"
            />
            <button
              type="button"
              onClick={() => copy(issued.accessTokenId)}
              className="rounded border px-2 py-1 text-xs"
            >
              Kopiér
            </button>
          </div>
        </label>
        <label className="mb-4 block text-sm">
          <span className="mb-1 block text-ink-2">Client Secret</span>
          <div className="flex gap-2">
            <input
              readOnly
              value={issued.clientSecret}
              className="flex-1 rounded border border-line-2 bg-paper-1 px-2 py-1 font-mono text-xs"
            />
            <button
              type="button"
              onClick={() => copy(issued.clientSecret)}
              className="rounded border px-2 py-1 text-xs"
            >
              Kopiér
            </button>
          </div>
        </label>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-primary px-3 py-1 text-sm text-white"
          >
            Luk
          </button>
        </div>
      </div>
    </div>
  );
}
